import { AnyEventObject, DoneActorEvent } from 'xstate';
import { toStoredFacilityName } from '~/features/market/facility/utils';
import { EmployerFacility } from '~/types/hlx/employerFacility';
import { MsaasJobListingFacility } from '~/types/hlx/msaasJobListingFacility';
import { Context, OnLoadEvent, OnSubmitEvent, Scope } from './types';

type SubmitResult = PromiseFulfilledResult<unknown>;

const isFacilitySubmitResult = (
  promise: SubmitResult
): promise is PromiseFulfilledResult<MsaasJobListingFacility[]> => {
  if (!Array.isArray(promise.value)) {
    return false;
  }

  const [first] = promise.value;
  return (
    first === undefined ||
    (typeof first === 'object' && first !== null && 'employerFacilityId' in first)
  );
};

export const setFacility = ({ event }: Scope): Partial<Context> => {
  const { facility } = event as AnyEventObject;
  return {
    // [MCV-1876] The created facility arrives with the picker label ("Name
    // (CITY, ST)"); the dialog prefills facilityName from this, so store the
    // facility's own name or the suffix gets saved again.
    facility: { facilityName: toStoredFacilityName(facility), facilityId: facility.value },
  };
};

export const setEmployerFacility = ({ event }: Scope): Partial<Context> => {
  const { output } = event as unknown as DoneActorEvent<EmployerFacility, string>;
  return {
    employerFacility: output,
  };
};

export const setLoading = (): Partial<Context> => ({ loading: true });

export const resetLoading = (): Partial<Context> => ({ loading: false });

export const openModal = (): Partial<Context> => ({ modal: true });

export const closeModal = (): Partial<Context> => ({ modal: false });

export const setListingId = ({ event }: Scope): Partial<Context> => {
  const { payload } = event as OnLoadEvent;
  return {
    listingId: payload.listingId,
  };
};

export const setMsaasJobListingFacilities = ({ event }: Scope): Partial<Context> => {
  const { output } = event as AnyEventObject;
  const results: SubmitResult[] = Array.isArray(output) ? output : [];
  const addFacility = results.find(isFacilitySubmitResult);
  return {
    msaasJobListingFacilities: addFacility?.value ?? [],
    msaasJobListingFacilitiesSubmitted: addFacility !== undefined,
  };
};

// [MCV-1523] Runs on ON_SUBMIT and records what the step submitted, so the
// parent listing can be told about it once the API calls resolve.
export const setSubmittedStepValues = ({ event }: Scope): Partial<Context> => {
  const { payload } = event as OnSubmitEvent;
  return {
    states: payload.states,
    primaryFacility: payload.primaryFacility,
  };
};

export const removeFacilitiesFromContext = ({ context }: Scope): Partial<Context> => {
  return {
    msaasJobListingFacilities: context.msaasJobListingFacilities.filter(
      (facility) => !context.facilitiesToDelete.includes(facility.employerFacilityId)
    ),
  };
};
